import type {
    FC,
    FocusEventHandler,
    KeyboardEvent,
    MouseEventHandler,
    ReactNode,
    Ref,
} from 'react';
import React, { useCallback, useEffect, useRef } from 'react';

import type { IWithControlProps } from '../hocs/withControl';
import { withControl } from '../hocs/withControl';
import type { IWithControlProps as IWithControlDesktopProps } from '../hocs/withControl@desktop';
import { withControl as withDesktopControl } from '../hocs/withControl@desktop';
import { useUniqueId, useUpdatableState } from '../hooks';
import { isKeyCode, Keys } from '../lib/keyboard';
import { mergeRefs } from '../lib/mergeRefs';
import { Palettes } from '../PaletteGeneration';
import { Text } from '../Text';
import { CheckboxBox as Box } from './Box/Checkbox-Box';
import { cnCheckbox } from './Checkbox.cn';
import type { ICheckboxControlProps } from './Control/Checkbox-Control';
import { CheckboxControl as Control } from './Control/Checkbox-Control';
import { CheckboxTick as Tick } from './Tick/Checkbox-Tick';

import './Checkbox.css';

export type CheckboxControlProps = ICheckboxControlProps;

export interface CheckboxProps
    extends
        IWithControlProps<HTMLInputElement>,
        IWithControlDesktopProps<HTMLInputElement>,
        CheckboxControlProps {
    /**
     * Событие, которое вызывается при потере фокуса компонентом. Например, при клике на другом месте экрана
     */
    onBlur?: FocusEventHandler<HTMLElement>;

    /**
     * Событие, которое вызывается при нажатии на компонент
     */
    onClick?: MouseEventHandler<HTMLElement>;

    /**
     * Событие, которое возникает при получении компонентом фокуса
     */
    onFocus?: FocusEventHandler<HTMLElement>;

    /**
     * Событие по своему действию похоже на `onClick` и возникает в момент нажатия кнопки мыши.
     * `onClick` в каком-то смысле является комбинацией событий `onMouseDown` и `onMouseUp`
     */
    onMouseDown?: MouseEventHandler<HTMLElement>;

    /**
     * Событие по своему действию противоположно событию `onMouseDown` и происходит при отпускании кнопки мыши.
     * Курсор должен находится в пределах HTML-элемента, к которому добавлен атрибут `onmouseup`
     */
    onMouseUp?: MouseEventHandler<HTMLElement>;

    /**
     * Обработчик события `onMouseLeave`
     */
    onMouseLeave?: MouseEventHandler<HTMLElement>;

    /**
     * Обработчик события `onMouseEnter`
     */
    onMouseEnter?: MouseEventHandler<HTMLElement>;

    /**
     * Состояние фокуса на компоненте
     */
    focused?: boolean;

    /**
     * Неактивное состояние компонента.
     * Состояние, при котором компонент отображается, но недоступен для действий пользователя
     */
    disabled?: boolean;

    /**
     * Состояние нажатия на компоненте
     */
    pressed?: boolean;

    /**
     * Состояние, которое возникает при наведении на компонент курсором
     */
    hovered?: boolean;

    /**
     * Ссылка на корневой DOM-элемент компонента
     */
    innerRef?: Ref<HTMLElement>;

    /**
     * Текст подписи к чекбоксу
     */
    label?: ReactNode;

    /**
     * Внешний вид чекбокса
     * @internal
     */
    view?: 'outline';

    /**
     * Визуально переводит чекбокс в неопределенное состояние. Не влияет на состояние, указанное в `checked`.
     *
     * Может использоваться в дереве чекбоксов, чтобы показать состояние родительского чекбокса,
     * когда хотя бы один вложенный чекбокс отмечен, но не все.
     *
     * Если свойство задано родительскому чекбоксу,
     * то в `aria-controls` необходимо добавить `id` всех вложенных чекбоксов
     */
    indeterminate?: boolean;

    /**
     * Состояние переключателя: включен или выключен
     */
    checked?: boolean;

    /**
     * Всплывающая подсказка
     */
    title?: string;

    /**
     * Устанавливает фокус в компонент при монтировании
     */
    autoFocus?: boolean;
    /**
     * Размер
     */
    size?: 'm';

    /**
     * Состояние ошибки
     */
    isError?: boolean;

    /**
     * Обязательное
     */
    isRequired?: boolean;

    /**
     * Подзаголовок, или текст ошибки
     */
    caption?: string;

    /**
     * Ширина
     * - `'hug'`: по размеру содержимого
     * - `'fill'`: по размеру парента
     * @default 'fill'
     */
    width?: 'hug' | 'fill';

    /**
     * Расположение чекбокса слева или справа от надписи
     * @default 'left'
     */
    align?: 'left' | 'right';
}

/**
 * Компонент для создания чекбоксов различных типов.
 *
 * @param {CheckboxProps} props
 */
const CheckboxPresenter: FC<CheckboxProps> = ({
    checked,
    className,
    controlRef: htmlControlRef = null,
    disabled,
    focused,
    hovered,
    id: givenId,
    indeterminate,
    innerRef,
    label,
    onMouseDown,
    onMouseUp,
    onMouseEnter,
    onMouseLeave,
    onKeyDown: htmlOnKeyDown,
    onKeyUp: htmlOnKeyUp,
    tabIndex,
    title,
    required,
    view: _view,
    size = 'm',
    isError: givenError,
    isRequired = false,
    caption,
    width = 'fill',
    align = 'left',
    pressed: givenPressed = false,
    ...props
}) => {
    const id = useUniqueId({ givenId });
    const [pressed, setPressed] = useUpdatableState(givenPressed);
    const controlRef = useRef<HTMLInputElement>(null);

    const isError = givenError && !disabled;

    useEffect(() => {
        if (controlRef.current !== null && indeterminate !== undefined) {
            controlRef.current.indeterminate = indeterminate;
        }
    }, [indeterminate]);

    const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (isKeyCode(event, [Keys.SPACE])) {
                setPressed(true);
            }
            if (htmlOnKeyDown !== undefined) {
                htmlOnKeyDown(event);
            }
        },
        [htmlOnKeyDown, setPressed],
    );

    const onKeyUp = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (isKeyCode(event, [Keys.SPACE])) {
                setPressed(false);
            }
            if (htmlOnKeyUp !== undefined) {
                htmlOnKeyUp(event);
            }
        },
        [htmlOnKeyUp, setPressed],
    );

    const labelId = `label-${id}`;

    return (
        <span
            className={cnCheckbox(
                {
                    checked: indeterminate || checked,
                    required: isRequired,
                    pressed,
                    focused,
                    disabled,
                    hovered,
                    indeterminate,
                    size,
                    width,
                    error: isError,
                    align,
                },
                [className],
            )}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            ref={innerRef}
            title={title}
        >
            <Box>
                <Control
                    {...props}
                    aria-checked={indeterminate ? 'mixed' : checked}
                    aria-labelledby={labelId}
                    checked={indeterminate || checked}
                    controlRef={mergeRefs(controlRef, htmlControlRef)}
                    disabled={disabled}
                    id={id}
                    onKeyDown={onKeyDown}
                    onKeyUp={onKeyUp}
                    tabIndex={tabIndex}
                    required={required}
                />
                <Tick
                    indeterminate={indeterminate}
                    className={
                        (indeterminate || checked) && disabled ? undefined : Palettes.inverse
                    }
                />
            </Box>
            {Boolean(label) && (
                <span className={cnCheckbox('Text')}>
                    <Text
                        as="label"
                        className={cnCheckbox('Label')}
                        color={disabled ? 'disabled' : 'primary'}
                        id={labelId}
                        htmlFor={id}
                        typography="bodyS"
                    >
                        {label}
                    </Text>
                    {caption && (
                        <Text
                            as="label"
                            className={cnCheckbox('Caption')}
                            // eslint-disable-next-line no-nested-ternary
                            color={disabled ? 'disabled' : isError ? 'error' : 'secondary'}
                            htmlFor={id}
                            typography="captionM"
                        >
                            {caption}
                        </Text>
                    )}
                </span>
            )}
        </span>
    );
};

CheckboxPresenter.displayName = cnCheckbox();

export const Checkbox = withDesktopControl(withControl(CheckboxPresenter));
